import { z } from 'zod';
/**
 * Phần cam kết của một người cho **một** dự án — APM-1310b.
 *
 * ## Vì sao có hai đơn vị, và vì sao lưu cả đơn vị đã chọn
 *
 * `4 giờ/ngày` và `50%` **không** phải hai cách viết cùng một điều. Giờ là con
 * số tuyệt đối: người đó dành đúng bốn tiếng, kể cả khi lịch gốc của họ đổi từ
 * tám xuống sáu tiếng. Phần trăm thì đi theo lịch gốc: chuyển sang bán thời
 * gian là mọi cam kết theo phần trăm tự co lại.
 *
 * Quy đổi về một đơn vị lúc lưu sẽ **quên** mất PM đã quyết điều nào, và cái
 * quên đó chỉ lộ ra khi lịch gốc đổi — lúc đó không còn ai để hỏi lại.
 *
 * ## Vì sao con số tính theo TỪNG THỨ, không phải một số mỗi ngày
 *
 * Lịch gốc của APM-1310a cho phép thứ Hai tám tiếng và thứ Sáu bốn tiếng. Nên
 * `50%` là bốn tiếng thứ Hai và hai tiếng thứ Sáu, còn `6 giờ/ngày` thì thứ Sáu
 * **không đủ chỗ**. Một con số duy nhất cho cả tuần phải chọn giữa làm tròn lên
 * (báo còn trống vào ngày đã kín) hoặc làm tròn xuống (báo kín vào ngày còn
 * trống); cả hai đều sai vào đúng những ngày quan trọng.
 *
 * ## Vì sao phép tính nằm ở đây chứ không ở API
 *
 * Cùng lý do như `weeklyHours` của lịch gốc: form của PM phải hiện "đặt số này
 * thì tổng thành 10/8 giờ" **ngay lúc gõ**, trước khi bấm lưu, còn server phải
 * tính đúng con số ấy để trả về sau khi lưu. Hai bản cài đặt của cùng một phép
 * cộng sẽ lệch nhau, và lệch vài giờ thì không ai phát hiện ra.
 */
export const CommitmentMode = z.enum(['hours', 'percent']);
/**
 * Cam kết: `hours` = giờ/ngày (0,5–24), `percent` = phần trăm lịch gốc (1–100).
 *
 * Không nhận `0`: "không cam kết gì" đã có cách diễn đạt riêng là **không có
 * cam kết** (`null`). Hai cách nói cùng một điều thì màn hình phải giải thích
 * cả hai, và người dùng sẽ thấy "0 giờ/ngày" nằm cạnh "chưa đặt".
 */
export const ProjectCommitment = z
    .object({ mode: CommitmentMode, value: z.number().positive() })
    .refine((commitment) => commitment.mode === 'hours'
    ? commitment.value >= 0.5 && commitment.value <= 24
    : commitment.value >= 1 && commitment.value <= 100, { message: 'Giờ/ngày phải trong khoảng 0,5–24; phần trăm phải trong khoảng 1–100' });
/** `null` = bỏ cam kết, trả người đó về "chưa đặt". */
export const SetProjectCommitmentInput = z.object({ commitment: ProjectCommitment.nullable() });
/**
 * Số giờ một cam kết chiếm trong một ngày có trần `capacityHours`.
 *
 * ⚠️ `hours` bị **chặn trên** bởi trần của chính ngày đó. Không chặn thì một
 * cam kết "6 giờ/ngày" đòi sáu tiếng ở một thứ Sáu chỉ làm bốn tiếng, và hệ
 * thống tự sinh ra hai tiếng quá tải mà không PM nào đặt ra. Ngày không làm
 * (trần 0) thì cam kết bằng 0 — nó không biến ngày nghỉ thành ngày làm.
 */
export function commitmentHoursOn(commitment, capacityHours) {
    if (capacityHours <= 0)
        return 0;
    const raw = commitment.mode === 'hours' ? commitment.value : (capacityHours * commitment.value) / 100;
    return Math.round(Math.min(raw, capacityHours) * 100) / 100;
}
/**
 * Cam kết của một người ở **những dự án khác**, kèm trần từ lịch gốc.
 *
 * ## ⚠️ Vì sao ở đây có con số nhưng có thể không có tên
 *
 * Cảnh báo cộng dồn chỉ có nghĩa khi nó cộng cả những dự án mà PM đang đặt
 * **không được vào** — đó là toàn bộ lý do lịch làm việc nằm ở cấp tổ chức
 * (spec mục 15). Nên ranh giới của `APM-504b` được giữ nguyên: trả **số**, và
 * chỉ trả tên những dự án mà người xem vốn đã vào được.
 *
 * `otherProjectCount` luôn là tổng thật; `otherProjectNames` có thể ngắn hơn.
 * Chênh lệch giữa hai con số **là** thông tin: "9/8 giờ ở 3 dự án (Thanh toán,
 * +2 dự án khác)" nói đủ để PM đi hỏi, mà không nói dự án nào.
 */
export const CommitmentRollup = z.object({
    /**
     * Trần từ lịch gốc theo thứ — 7 ô, `0` = Chủ nhật (đúng `Date.getUTCDay()`).
     *
     * Gửi cả mảng chứ không gửi một con số tổng: form phải tính lại tổng cho từng
     * thứ ngay lúc PM gõ, và nó không có cách nào tự biết lịch gốc của người kia.
     */
    baselineHours: z.array(z.number().nonnegative()).length(7),
    /** Cam kết ở các dự án **khác**, theo thứ. Cùng thứ tự chỉ số với `baselineHours`. */
    otherHours: z.array(z.number().nonnegative()).length(7),
    otherProjectCount: z.number().int().nonnegative(),
    /** Tên dự án khác mà người xem được phép biết. Ngắn hơn `otherProjectCount` là bình thường. */
    otherProjectNames: z.array(z.string()),
    /** `true` = người này chưa khai lịch, `baselineHours` là mặc định của hệ thống. */
    baselineIsDefault: z.boolean(),
});
/**
 * Tổng cam kết có vượt lịch gốc không, nếu có thì vượt ở thứ nào.
 *
 * Trả về thứ **tệ nhất** chứ không phải thứ đầu tiên vượt: một người kín thứ
 * Hai 2 giờ và thứ Sáu 6 giờ thì con số cần nói ra là 6, không phải 2.
 *
 * `null` = không vượt. Dùng cho cả xem trước lúc gõ (`commitment` là giá trị
 * đang gõ) và cho trạng thái đã lưu.
 */
export function commitmentOverflow(rollup, commitment) {
    let worst = null;
    for (let weekday = 0; weekday < 7; weekday += 1) {
        const capacityHours = rollup.baselineHours[weekday] ?? 0;
        const here = commitment === null ? 0 : commitmentHoursOn(commitment, capacityHours);
        const committedHours = Math.round(((rollup.otherHours[weekday] ?? 0) + here) * 100) / 100;
        // Ngày không làm mà có cam kết thì `commitmentHoursOn` đã trả 0 cho cả hai
        // phía, nên ở đây không cần tách trường hợp trần bằng 0.
        if (committedHours <= capacityHours + 0.01)
            continue;
        if (worst === null ||
            committedHours - capacityHours > worst.committedHours - worst.capacityHours) {
            worst = { weekday, committedHours, capacityHours };
        }
    }
    return worst;
}
//# sourceMappingURL=project-commitment.js.map